/**
 * SafeBottomArea Component
 * A reusable wrapper that provides bottom safe area padding for Android navigation bar
 * and iOS home indicator. Ensures bottom-positioned UI elements remain accessible.
 */

import React from 'react';
import { View, StyleSheet, ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import {
  createBottomPaddingStyle,
  resolveBottomSafePadding,
  safeAreaStyles,
} from '../styles/safeArea';

export interface SafeBottomAreaProps {
  /** Child components to render within the safe area */
  children: React.ReactNode;
  /** Additional styles to apply to the container */
  style?: ViewStyle;
  /** Background color for the safe area padding region */
  backgroundColor?: string;
  /** Test ID for testing purposes */
  testID?: string;
  /** Whether to apply platform-specific elevation/shadow */
  withElevation?: boolean;
}

/**
 * SafeBottomArea wraps content that appears at the bottom of the screen,
 * automatically applying padding to avoid overlap with system navigation bars.
 *
 * @example
 * ```tsx
 * <SafeBottomArea backgroundColor="#f5f5f5">
 *   <PaginationControls />
 * </SafeBottomArea>
 * ```
 */
export const SafeBottomArea: React.FC<SafeBottomAreaProps> = ({
  children,
  style,
  backgroundColor,
  testID = 'safe-bottom-area',
  withElevation = false,
}) => {
  const insets = useSafeAreaInsets();
  const bottomPaddingStyle = createBottomPaddingStyle(insets.bottom);

  const containerStyle: ViewStyle[] = [
    styles.container,
    bottomPaddingStyle,
    withElevation && safeAreaStyles.bottomContainer,
    backgroundColor ? { backgroundColor } : undefined,
    style,
  ].filter(Boolean) as ViewStyle[];

  return (
    <View style={containerStyle} testID={testID}>
      {children}
    </View>
  );
};

/**
 * Hook to get safe bottom padding value
 * Use this when you need the padding value directly instead of the wrapper component
 *
 * @returns The calculated bottom padding value
 */
export const useSafeBottomPadding = (): number => {
  const insets = useSafeAreaInsets();
  return resolveBottomSafePadding(insets.bottom);
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
});

export default SafeBottomArea;
